import { Button, Modal, RadioButton, RadioRatingStarButton } from '@ag1/ui';
import Select from '@portal/shared/ui/Select';
import { useCallback } from 'react';
import clsx from 'clsx';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { useGateValue } from '@statsig/react-bindings';
import { SSGates } from '@portal/state/context/StatsigContext/StatsigFlags';
import { useAGZAccount } from '@portal/state/hooks/useAGZAccount';
import { RatingCount } from '@portal/types/review';
import {
  FILTER_BY_OPTION_LIST,
  getUpdatedRatingOptions,
  REVIEW_PRODUCT_FLAVOR_OPTION_LIST,
  REVIEW_PRODUCT_FLAVOR_OPTION_LIST_AGZ,
  SORT_BY_OPTION_LIST
} from '../constants';
import styles from './FilterAndSortModal.module.scss';

interface FormFields {
  modalSortBy: string;
  modalFilteredBy: string;
  modalFilteredByFlavors?: string;
  modalFilteredByRating?: string | number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (formValues: {
    modalSortBy: string;
    modalFilteredBy: string;
    modalFilteredByFlavors?: string;
    modalFilteredByRating?: string | number;
  }) => void;
  form: {
    initialValues: {
      sortByValue: string;
      filteredByValue: string;
      filteredByFlavorsValue: string;
      filteredByRatingValue: string;
    };
  };
  ratingCounts: RatingCount[];
  onMobileFilterBySelect: (formValues: { filteredBy: string; filteredByFlavors: string }) => void;
}

export const FilterAndSortModal = ({
  isOpen,
  onClose,
  onApply,
  form: {
    initialValues: { sortByValue, filteredByValue, filteredByFlavorsValue, filteredByRatingValue }
  },
  ratingCounts,
  onMobileFilterBySelect
}: Props) => {
  const { register, watch, handleSubmit, control } = useForm({
    defaultValues: {
      modalSortBy: sortByValue,
      modalFilteredBy: filteredByValue,
      modalFilteredByFlavors: filteredByFlavorsValue,
      modalFilteredByRating: filteredByRatingValue === 'all' ? 'all' : Number(filteredByRatingValue)
    }
  });

  const modalSortByValue = watch('modalSortBy');
  const modalFilteredByValue = watch('modalFilteredBy');
  const modalFilteredByFlavorsValue = watch('modalFilteredByFlavors');
  const modalFilteredByRatingValue = watch('modalFilteredByRating');

  const { isAgzGateEnabled: enableAGZ } = useAGZAccount();
  const enableFlavor = useGateValue(SSGates.enableFlavorSelection);

  const onSubmit: SubmitHandler<FormFields> = useCallback(
    ({ modalSortBy, modalFilteredBy, modalFilteredByFlavors, modalFilteredByRating }) => {
      onApply({ modalSortBy, modalFilteredBy, modalFilteredByFlavors, modalFilteredByRating });
      setTimeout(() => onClose(), 200);
    },
    [onApply, onClose]
  );

  const onClear = useCallback(() => {
    onApply({ modalSortBy: 'rating', modalFilteredBy: 'all', modalFilteredByRating: 'all' });
    setTimeout(() => onClose(), 200);
  }, [onApply, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} animation="slide" className={styles.modal}>
      <Modal.Header className={styles.headerGroup}>
        <h3 className={styles.title}>FILTER & SORT</h3>
      </Modal.Header>
      <Modal.Content>
        <div className={styles.filters}>
          <div className={styles.filterContent}>
            <div className={clsx(styles.filterGroup, styles.sortByGroup)}>
              <h4>Sort by</h4>
              {SORT_BY_OPTION_LIST.map((option) => (
                <RadioButton
                  key={`modal_${option.id}`}
                  className={clsx(
                    styles.radio,
                    option.value === modalSortByValue && styles.isSelected
                  )}
                  {...register('modalSortBy')}
                  {...option}
                  id={`modal_${option.id}`}
                />
              ))}
            </div>
            <div className={clsx(styles.filterGroup, styles.filteredByGroup)}>
              {enableFlavor && (
                <>
                  <h4>What product(s) would you like to review?</h4>
                  <Controller
                    name="modalFilteredBy"
                    control={control}
                    render={({ field: { onChange, value: formValue, ...rest } }) => (
                      <Select
                        {...rest}
                        id="modalFilteredBy"
                        placeholder="Select Product"
                        options={
                          enableAGZ
                            ? FILTER_BY_OPTION_LIST
                            : FILTER_BY_OPTION_LIST.filter((option) => option.value !== 'ag-agz')
                        }
                        className={clsx(
                          styles.filterBySelect,
                          modalFilteredByValue === formValue && styles.isSelected
                        )}
                        value={formValue}
                        onChange={(value: string) => {
                          onChange(value);
                          onMobileFilterBySelect({
                            filteredBy: value,
                            filteredByFlavors: modalFilteredByFlavorsValue
                          });
                        }}
                        mobileOverflow
                      />
                    )}
                  />
                </>
              )}

              {((enableFlavor && modalFilteredByValue === 'ag1-next-gen') ||
                (enableAGZ && modalFilteredByValue === 'ag-agz')) && (
                <div className={styles.flavorFilter}>
                  <h4>{`What flavor(s) of ${FILTER_BY_OPTION_LIST.find(
                    (option) => option.value === modalFilteredByValue
                  )?.label} would you like to view?`}</h4>
                  {(enableAGZ && modalFilteredByValue === 'ag-agz'
                    ? REVIEW_PRODUCT_FLAVOR_OPTION_LIST_AGZ
                    : REVIEW_PRODUCT_FLAVOR_OPTION_LIST
                  ).map((option) => (
                    <Controller
                      key={option.id}
                      name="modalFilteredByFlavors"
                      control={control}
                      render={({ field: { onChange, ...rest } }) => (
                        <RadioButton
                          key={option.id}
                          className={clsx(
                            styles.radio,
                            modalFilteredByFlavorsValue === option.value && styles.isSelected
                          )}
                          {...rest}
                          {...option}
                          onChange={(e) => {
                            onChange(e.target.value);
                            onMobileFilterBySelect({
                              filteredBy: modalFilteredByValue,
                              filteredByFlavors: option.value
                            });
                          }}
                        />
                      )}
                    />
                  ))}
                </div>
              )}

              {enableAGZ && (
                <div className={styles.ratingFilter}>
                  <h4>Rating</h4>
                  {getUpdatedRatingOptions(ratingCounts).map((option) => (
                    <Controller
                      key={option.id}
                      name="modalFilteredByRating"
                      control={control}
                      render={({ field: { onChange, ...rest } }) => (
                        <RadioRatingStarButton
                          key={option.id}
                          rating={option.value !== 'all' ? option.value : undefined}
                          ratingLabel={option.count?.toString()}
                          className={clsx(
                            styles.radio,
                            modalFilteredByRatingValue === option.value && styles.isSelected
                          )}
                          {...rest}
                          {...option}
                          onChange={(e) => {
                            e.stopPropagation();
                            onChange(e.target.value);
                          }}
                          id={`modal_${option.id}`}
                        />
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className={styles.footerButtonGroup}>
          <Button
            theme="new"
            className={styles.btn}
            onClick={handleSubmit(onSubmit)}
            size={{ xxs: 'medium' }}
            rounded>
            Apply Filters
          </Button>
          <Button
            theme="new"
            className={styles.btn}
            variant="link"
            onClick={onClear}
            size={{ xxs: 'medium' }}>
            Clear All
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
